/* eslint-disable react-refresh/only-export-components */
import {
  TrendingUp,
  GripVertical,
  ArrowDownUp,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  SortableContext,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortableColumn } from '~/components/ui/table';

export type ClickSortDirection = 'asc' | 'desc' | null;

// Canned-tally rows have no facility column and no real length value to sort by (every row's
// length cell is a static placeholder); price/stock/leadTime are genuine per-group backend
// aggregates and are sorted server-side.
export const CANNED_TALLY_UNSORTABLE_FIELDS = new Set(['length', 'facility']);

export interface TableHeaderDefinition {
  key: string;
  label: string;
  showTrending: boolean;
  clickSortable?: boolean;
  stickTo?: 'left' | 'right' | 'none';
}

export function nextSortDirection(
  currentKey: string | null,
  currentDirection: ClickSortDirection,
  clickedKey: string
): ClickSortDirection {
  if (currentKey !== clickedKey) return 'asc';
  if (currentDirection === 'asc') return 'desc';
  if (currentDirection === 'desc') return null;
  return 'asc';
}

export interface TableHeaderProps {
  children: React.ReactNode;
  showTrendingIcon?: boolean;
  stickTo?: 'left' | 'right' | 'none';
  sortDirection?: ClickSortDirection;
  onSortClick?: () => void;
}

function headerClassName(stickTo: 'left' | 'right' | 'none') {
  return `px-6 py-3 text-left text-xs font-medium text-neut-500 uppercase tracking-wider select-none ${stickTo === 'right' ? 'sticky right-0 bg-neut-50 z-[1]' : stickTo === 'left' ? 'sticky left-0 bg-neut-50 z-[1]' : ''}`;
}

function ariaSortValue(
  onSortClick: (() => void) | undefined,
  sortDirection: ClickSortDirection | undefined
): 'ascending' | 'descending' | 'none' | undefined {
  if (!onSortClick) return undefined;
  if (sortDirection === 'asc') return 'ascending';
  if (sortDirection === 'desc') return 'descending';
  return 'none';
}

function sortClickIcon(direction: ClickSortDirection) {
  if (direction === 'asc')
    return <ArrowUp className="w-3.5 h-3.5" aria-hidden />;
  if (direction === 'desc')
    return <ArrowDown className="w-3.5 h-3.5" aria-hidden />;
  return <ArrowDownUp className="w-3.5 h-3.5 text-neut-300" aria-hidden />;
}

function HeaderLabel({
  children,
  showTrendingIcon,
  sortDirection,
  onSortClick,
}: Readonly<{
  children: React.ReactNode;
  showTrendingIcon: boolean;
  sortDirection?: ClickSortDirection;
  onSortClick?: () => void;
}>) {
  if (!onSortClick) {
    return (
      <>
        {children}
        {showTrendingIcon && (
          <TrendingUp className="w-3.5 h-3.5 text-prim" aria-hidden="true" />
        )}
      </>
    );
  }
  return (
    <button
      type="button"
      className="flex items-center gap-1 cursor-pointer hover:bg-neut-100 -mx-1 px-1 py-0.5 rounded"
      onClick={onSortClick}
    >
      {children}
      {showTrendingIcon && (
        <TrendingUp className="w-3.5 h-3.5 text-prim" aria-hidden="true" />
      )}
      {sortClickIcon(sortDirection ?? null)}
    </button>
  );
}

export const TableHeader = ({
  children,
  showTrendingIcon = false,
  stickTo = 'none',
  sortDirection,
  onSortClick,
}: Readonly<TableHeaderProps>) => (
  <th
    className={headerClassName(stickTo)}
    aria-sort={ariaSortValue(onSortClick, sortDirection)}
  >
    <div className="flex items-center gap-1">
      <HeaderLabel
        showTrendingIcon={showTrendingIcon}
        sortDirection={sortDirection}
        onSortClick={onSortClick}
      >
        {children}
      </HeaderLabel>
    </div>
  </th>
);

export function SortableTableHeader({
  columnKey,
  children,
  showTrendingIcon = false,
  sortDirection,
  onSortClick,
}: Readonly<TableHeaderProps & { columnKey: string }>) {
  const { t } = useTranslation('general');
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, style } =
    useSortableColumn(columnKey);

  return (
    <th
      ref={setNodeRef}
      style={style}
      className={headerClassName('none')}
      aria-sort={ariaSortValue(onSortClick, sortDirection)}
    >
      <div className="flex items-center gap-1">
        <button
          ref={setActivatorNodeRef}
          type="button"
          {...attributes}
          {...listeners}
          className="shrink-0 text-neut-400 cursor-grab"
          aria-label={t('table.reorder_column')}
          data-testid={`column-grip-${columnKey}`}
        >
          <GripVertical size={14} aria-hidden />
        </button>
        <HeaderLabel
          showTrendingIcon={showTrendingIcon}
          sortDirection={sortDirection}
          onSortClick={onSortClick}
        >
          {children}
        </HeaderLabel>
      </div>
    </th>
  );
}

export function ComparisonTableHeadRow({
  activeHeaders,
  unpinnedColumnKeys,
  activeSortKey,
  activeSortDirection,
  onSort,
  onSortClick,
  leadingHeader,
}: Readonly<{
  activeHeaders: TableHeaderDefinition[];
  unpinnedColumnKeys: string[];
  activeSortKey: string | null;
  activeSortDirection: ClickSortDirection;
  onSort?: (field: string, direction: 'asc' | 'desc' | null) => void;
  onSortClick: (columnKey: string) => void;
  leadingHeader?: React.ReactNode;
}>) {
  return (
    <thead className="bg-neut-50">
      <tr>
        {leadingHeader}
        <SortableContext
          items={unpinnedColumnKeys}
          strategy={horizontalListSortingStrategy}
        >
          {activeHeaders.map((header) =>
            header.stickTo === 'right' ? (
              <TableHeader
                key={header.key}
                showTrendingIcon={header.showTrending}
                stickTo="right"
              >
                {header.label}
              </TableHeader>
            ) : (
              <SortableTableHeader
                key={header.key}
                columnKey={header.key}
                showTrendingIcon={header.showTrending}
                sortDirection={
                  activeSortKey === header.key ? activeSortDirection : null
                }
                onSortClick={
                  header.clickSortable && onSort
                    ? () => onSortClick(header.key)
                    : undefined
                }
              >
                {header.label}
              </SortableTableHeader>
            )
          )}
        </SortableContext>
      </tr>
    </thead>
  );
}
